import React from "react";
import { VERTICALS } from "@/data/verticals";
import CTAStrip from "@/components/CTAStrip";
import { Reveal } from "@/components/Reveal";
import PageHead from "@/components/PageHead";

const TOP_STATS = [
  { v: "8.2M+", l: "Leads delivered annually" },
  { v: "48", l: "Active partners" },
  { v: "<60s", l: "Median delivery latency" },
  { v: "98%", l: "Consent compliance rate" },
];

const BENCHMARKS = [
  { vertical: "Auto", contact: "38%", quote: "21%", bind: "9.4%" },
  { vertical: "Home", contact: "41%", quote: "23%", bind: "10.1%" },
  { vertical: "Life", contact: "44%", quote: "27%", bind: "8.2%" },
  { vertical: "Health", contact: "39%", quote: "22%", bind: "11.8%" },
  { vertical: "Medicare", contact: "47%", quote: "29%", bind: "14.2%" },
  { vertical: "Final Expense", contact: "52%", quote: "31%", bind: "12.5%" },
  { vertical: "Solar", contact: "36%", quote: "19%", bind: "6.4%" },
  { vertical: "Home Services", contact: "58%", quote: "34%", bind: "18.1%" },
];

const Bar = ({ pct }) => (
  <div className="relative h-2 w-full bg-citirex-border">
    <div
      className="absolute inset-y-0 left-0 bg-citirex-accent"
      style={{ width: pct }}
    />
  </div>
);

export default function Performance() {
  return (
    <main data-testid="performance-page">
      <PageHead
        title="Performance"
        description="The numbers behind bind-rate-first media buying — Citirex platform stats and per-vertical benchmark performance across Auto, Home, Life, Health, Medicare, Final Expense, Solar and Home Services."
        path="/performance"
      />
      <section className="border-b border-citirex-border bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 pb-16 pt-12 md:px-12 md:pb-24 md:pt-20">
          <Reveal>
            <p className="overline">Performance</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 max-w-5xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter md:text-7xl lg:text-[110px]">
              The numbers behind{" "}
              <span className="font-editorial italic font-normal text-citirex-accent">
                bind-rate-first
              </span>{" "}
              media buying.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="bg-citirex-ink text-citirex-inv">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
          <p className="overline">Platform stats</p>
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
            {TOP_STATS.map((s) => (
              <div key={s.l} className="bg-citirex-ink p-6 md:p-8">
                <p className="font-editorial text-5xl font-medium leading-none md:text-7xl">
                  {s.v}
                </p>
                <p className="mt-6 font-body text-xs uppercase tracking-[0.2em] text-citirex-inv/70">
                  {s.l}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-citirex-bg">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <p className="overline">Benchmarks</p>
              <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Contact, quote, bind —{" "}
                <span className="font-editorial italic">across the book</span>.
              </h2>
              <p className="mt-6 font-body text-base text-citirex-sub md:text-lg">
                Funnel performance averaged across active partners and
                trailing 90 days. Your mileage will vary by appetite,
                geography and follow-up cadence.
              </p>
            </div>
            <div className="md:col-span-7">
              <div
                className="border border-citirex-border bg-citirex-surface"
                data-testid="performance-benchmark-table"
              >
                <div className="grid grid-cols-12 gap-4 border-b border-citirex-border bg-citirex-bg2 px-6 py-4">
                  <div className="col-span-4 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Vertical
                  </div>
                  <div className="col-span-3 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Contact
                  </div>
                  <div className="col-span-3 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Quote
                  </div>
                  <div className="col-span-2 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                    Bind
                  </div>
                </div>
                {BENCHMARKS.map((b) => (
                  <div
                    key={b.vertical}
                    className="grid grid-cols-12 items-center gap-4 border-b border-citirex-border px-6 py-5 last:border-b-0"
                  >
                    <div className="col-span-4 font-display text-base font-medium text-citirex-ink md:text-lg">
                      {b.vertical}
                    </div>
                    <div className="col-span-3">
                      <p className="font-editorial text-lg italic text-citirex-ink">
                        {b.contact}
                      </p>
                      <div className="mt-2">
                        <Bar pct={b.contact} />
                      </div>
                    </div>
                    <div className="col-span-3">
                      <p className="font-editorial text-lg italic text-citirex-ink">
                        {b.quote}
                      </p>
                      <div className="mt-2">
                        <Bar pct={b.quote} />
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="font-editorial text-lg italic text-citirex-accent">
                        {b.bind}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-citirex-border bg-citirex-bg2">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
          <p className="overline">By vertical</p>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-[1.05] md:text-6xl">
            Volume and qualifier depth, per book.
          </h2>
          <div className="mt-16 grid grid-cols-1 gap-px bg-citirex-border md:grid-cols-2 lg:grid-cols-4">
            {VERTICALS.map((v) => (
              <div
                key={v.slug}
                className="bg-citirex-bg2 p-6 md:p-8"
                data-testid={`performance-vertical-${v.slug}`}
              >
                <p className="font-editorial text-sm italic text-citirex-sub">
                  {v.index}
                </p>
                <h3 className="mt-2 font-display text-xl font-medium text-citirex-ink md:text-2xl">
                  {v.name}
                </h3>
                <p className="mt-6 font-editorial text-4xl italic text-citirex-accent md:text-5xl">
                  {v.stats[0].value}
                </p>
                <p className="mt-2 font-body text-xs uppercase tracking-[0.2em] text-citirex-sub">
                  {v.stats[0].label}
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3 border-t border-citirex-border pt-4">
                  {v.stats.slice(1, 3).map((s) => (
                    <div key={s.label}>
                      <p className="font-display text-base font-medium text-citirex-ink">
                        {s.value}
                      </p>
                      <p className="mt-1 font-body text-[10px] uppercase tracking-[0.15em] text-citirex-sub">
                        {s.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTAStrip
        eyebrow="Run the math"
        title="Show us your CPA. We'll show you the floor."
        buttonText="Book a pricing call"
      />
    </main>
  );
}
